import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useNavigate } from 'react-router-dom';
import { FiX, FiCamera, FiLoader, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

const QRScanner = ({ onClose, onScan }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const html5QrCodeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' }, // Use back camera
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText, decodedResult) => {
          handleScanSuccess(decodedText, decodedResult);
        },
        (errorMessage) => {
          // Ignore scanning errors (they're frequent during scanning)
        }
      );

      setScanning(true);
      setError(null);
    } catch (err) {
      console.error('Error starting QR scanner:', err);
      setError('Failed to start camera. Please check permissions.');
      setScanning(false);
    }
  };

  const stopScanning = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current
        .stop()
        .then(() => {
          html5QrCodeRef.current.clear();
          html5QrCodeRef.current = null;
          setScanning(false);
        })
        .catch((err) => {
          console.error('Error stopping scanner:', err);
        });
    }
  };

  const handleScanSuccess = (decodedText, decodedResult) => {
    setResult(decodedText);
    stopScanning();

    // Handle different QR code types
    if (onScan) {
      onScan(decodedText, decodedResult);
    } else {
      handleQRCode(decodedText);
    }
  };

  const handleQRCode = (text) => {
    // Check if it's a HubID profile URL
    if (text.includes('/u/') || text.includes('/#/u/')) {
      const usernameMatch = text.match(/\/u\/([^/?]+)/);
      if (usernameMatch) {
        const username = usernameMatch[1];
        navigate(`/u/${username}`);
        toast.success(`Navigating to ${username}'s profile`);
        if (onClose) onClose();
        return;
      }
    }

    // Check if it's a vCard
    if (text.startsWith('BEGIN:VCARD')) {
      handleVCard(text);
      return;
    }

    // Check if it's a URL
    if (text.startsWith('http://') || text.startsWith('https://')) {
      window.open(text, '_blank');
      toast.success('Opening URL from QR code');
      if (onClose) onClose();
      return;
    }

    // Generic text
    toast.success('QR code scanned!', {
      duration: 3000,
    });
    console.log('QR Code content:', text);
  };

  const handleVCard = (vcardText) => {
    // Parse vCard (simplified)
    const nameMatch = vcardText.match(/FN:(.+)/);
    const emailMatch = vcardText.match(/EMAIL[^:]*:(.+)/);
    const phoneMatch = vcardText.match(/TEL[^:]*:(.+)/);
    const urlMatch = vcardText.match(/URL[^:]*:(.+)/);

    const contact = {
      name: nameMatch ? nameMatch[1].trim() : null,
      email: emailMatch ? emailMatch[1].trim() : null,
      phone: phoneMatch ? phoneMatch[1].trim() : null,
      url: urlMatch ? urlMatch[1].trim() : null,
    };

    toast.success(`Scanned contact: ${contact.name || 'Unknown'}`);
    console.log('vCard contact:', contact);
    
    // Could open a contact import dialog here
    if (onClose) onClose();
  };

  const handleStartScan = () => {
    setScanning(true);
    setError(null);
    setResult(null);
  };

  const handleStopScan = () => {
    stopScanning();
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="glass-dark p-6 rounded-xl border border-dark-700 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-lime-bright">Scan QR Code</h2>
          <button
            onClick={handleStopScan}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        {/* Scanner Area */}
        <div className="relative mb-4">
          {!scanning ? (
            <div className="aspect-square bg-dark-800 rounded-lg border-2 border-dashed border-dark-700 flex items-center justify-center">
              <div className="text-center">
                <SafeIcon icon={FiCamera} className="text-4xl text-dark-500 mx-auto mb-4" />
                <p className="text-dark-400 mb-4">Click to start scanning</p>
                <button
                  onClick={handleStartScan}
                  className="glass-lime text-lime-400 px-6 py-3 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 hover:border-lime-400/70 shadow-glow-lime-sm inline-flex items-center"
                >
                  <SafeIcon icon={FiCamera} className="mr-2" />
                  Start Scanner
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <div id="qr-reader" className="rounded-lg overflow-hidden"></div>
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-64 h-64 border-2 border-lime-500 rounded-lg"></div>
              </div>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="text-sm text-dark-400 mb-4">
          <p>• Point camera at QR code</p>
          <p>• HubID profiles will open automatically</p>
          <p>• vCards and URLs are also supported</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-lime-500/20 border border-lime-500/50 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <SafeIcon icon={FiCheck} className="text-lime-400 mr-2" />
              <p className="text-lime-400 text-sm">Scanned successfully!</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-3">
          {scanning && (
            <button
              onClick={handleStopScan}
              className="flex-1 glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700"
            >
              Stop Scanning
            </button>
          )}
          {!scanning && (
            <button
              onClick={handleStopScan}
              className="flex-1 glass-lime text-lime-400 px-4 py-2 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;

